import React, { useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { ActionButton } from 'modules/ui/atoms/ActionButton'
import useFlow from 'modules/flow/state/use-flow'
import useOrder from 'modules/order/state/order/use-order'
import useStatus from 'modules/status/state/use-status'
import { StatusType } from 'modules/status/state/status-types'
import {
  trackFormSubmitEvent,
  trackFormErrorEvent,
} from 'modules/analytics/helpers/track-form-events'
import { PaymentSelectionButtonProps } from './payment-selection-button'
import { InlineCreditCardInputsRef } from './inline-credit-card-inputs'
import { DonorInfoFormData } from './donor-info-fields'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { useTranslation } from 'next-i18next'
import { getClassySessionId } from 'modules/utils/getClassySessionId'

// Global refs to store form references
let globalCreditCardFormRef: React.RefObject<InlineCreditCardInputsRef> | null = null
let globalDonorInfoFormRef: UseFormReturn<DonorInfoFormData> | null = null

export const setCreditCardFormRef = (ref: React.RefObject<InlineCreditCardInputsRef>) => {
  globalCreditCardFormRef = ref
}

export const setDonorInfoFormRef = (formRef: UseFormReturn<DonorInfoFormData>) => {
  globalDonorInfoFormRef = formRef
}

const CreditCardActionButton = (props: PaymentSelectionButtonProps) => {
  const flow = useFlow()
  const order = useOrder()
  const status = useStatus()
  const { t } = useTranslation()

  const onNext = useCallback(async () => {
    trackFormSubmitEvent(flow.currentStep.name, { payment_type: order.paymentDetails.type })

    try {
      status.set(StatusType.SUBMITTING)

      // Validate both donor info and credit card simultaneously
      const validationResults = await Promise.allSettled([
        new Promise<{ success: boolean; error?: string }>((resolve) => {
          if (globalDonorInfoFormRef) {
            globalDonorInfoFormRef.handleSubmit(
              () => {
                resolve({ success: true })
              },
              () => {
                resolve({ success: false, error: 'Please fill in all required fields' })
              },
            )()
          } else {
            resolve({ success: true })
          }
        }),

        new Promise<{ success: boolean; error?: string }>(async (resolve) => {
          if (globalCreditCardFormRef?.current) {
            const error = await globalCreditCardFormRef.current.savePaymentMethodToStore()

            if (error) {
              resolve({ success: false, error: error.message })
            } else {
              resolve({ success: true })
            }
          } else {
            resolve({ success: false, error: 'Credit card form is not available for validation.' })
          }
        }),
      ])

      // Check results from both validations
      const donorInfoResult = validationResults[0]
      const creditCardResult = validationResults[1]

      const donorInfoValid = donorInfoResult.status === 'fulfilled' && donorInfoResult.value.success

      const creditCardValid =
        creditCardResult.status === 'fulfilled' && creditCardResult.value.success

      // If either validation failed, show error and stop
      if (!donorInfoValid || !creditCardValid) {
        let errorMessage = 'Please correct the errors in the form'

        // Prioritize donor info errors, then credit card errors
        if (!donorInfoValid && donorInfoResult.status === 'fulfilled') {
          errorMessage = donorInfoResult.value.error || errorMessage
        } else if (!creditCardValid && creditCardResult.status === 'fulfilled') {
          errorMessage = creditCardResult.value.error || errorMessage
        }

        trackFormErrorEvent(flow.currentStep.name, errorMessage)
        status.set(StatusType.FAILED, errorMessage)
        return
      }

      // Both validations passed - save data to Redux store
      if (globalDonorInfoFormRef) {
        const donorData = globalDonorInfoFormRef.getValues()

        // Save contact info (email, firstName, lastName)
        order.contact.setValue({
          ...order.contact.value,
          firstName: donorData.firstName,
          lastName: donorData.lastName,
          email: donorData.email,
        })

        // Save address info (country, zipCode)
        order.address.setValue({
          ...order.address.value,
          country: donorData.country,
          zipCode: donorData.zipCode,
        })
      }

      // Track successful payment method selection
      if (window.newrelic) {
        window.newrelic.addPageAction('payment_method_selected', {
          'classy-session-id': getClassySessionId(),
          paymentMethod: order.paymentDetails.type,
        })
      }

      // Navigate to next step
      const nextStep = flow.nextStep

      if (nextStep) {
        flow.goToStep(nextStep.name)
      }

      status.set(StatusType.IDLE)
    } catch (e) {
      console.error(e)
      Bugsnag.notify(e as NotifiableError)

      status.set(
        StatusType.FAILED,
        t(
          'CreditCardActionButton.errorValidatePaymentMethod',
          'An error has occurred while trying to validate your payment method.',
        ),
      )
    }
  }, [flow, order.paymentDetails.type, order.contact, order.address, status, t])

  return (
    <ActionButton
      onClick={onNext}
      disabled={status.type === StatusType.SUBMITTING || status.type === StatusType.PROCESSING}
      {...props}
    />
  )
}

export default CreditCardActionButton
